import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "../../../../utils/apiConfig";
import PipelineCard from "./summary-action-items/PipelineCard";
import FinancialHealthCard from "../FinancialHealthCard";

import logger from '../../../../utils/logger';

const SummaryActionItems = () => {
  const { householdId, userId } = useParams();
  const [pipelineData, setPipelineData] = useState({
    stages: [],
    currentStage: null,
  });

  // Add pipeline data fetching
  useEffect(() => {
    const fetchPipelineData = async () => {
      if (!householdId || !userId) return;

      try {
        const [stagesResponse, statusResponse] = await Promise.all([
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/pipeline/stages`,
          ),
          api.get(
            `/advisor-portal/client-database/household-profile/${householdId}/client-profile/${userId}/pipeline`,
          ),
        ]);

        setPipelineData({
          stages: stagesResponse.data || [],
          currentStage: statusResponse.data || null,
        });
      } catch (error) {
        logger.error("Error fetching pipeline data:", error);
      }
    };

    fetchPipelineData();
  }, [householdId, userId]);

  return (
    <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow space-y-8">
      {/* Page Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Summary & Action Items
          </h1>
        </div>
      </div>

      {/* Financial Goals Summary */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Financial Goals Summary</h3>
        <p className="text-gray-700 dark:text-gray-300">
          Client aims to retire by 2045 with $2M in savings. Short-term goals
          include building emergency fund and paying off student loans. Mid-term
          goals focus on college savings for children.
        </p>
      </div>

      {/* Pipeline Card */}
      <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow p-6">
        <PipelineCard
          householdId={householdId}
          userId={userId}
          pipelineData={pipelineData}
        />
      </div>

      {/* Household Income Snapshot */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Household Income Snapshot
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Current Gross Income
              </label>
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">
                <i
                  className="fas fa-info-circle"
                  title="Before taxes and deductions"
                ></i>
              </span>
            </div>
            <div className="mt-1">
              <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                $120,000
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">Annual</div>
            </div>
          </div>
          <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Current Net Income
              </label>
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">
                <i
                  className="fas fa-info-circle"
                  title="After taxes and deductions"
                ></i>
              </span>
            </div>
            <div className="mt-1">
              <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                $85,000
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-500">Annual</div>
            </div>
          </div>
          <div className="bg-surface-default dark:bg-surface-default p-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Tax Bracket
              </label>
              <span className="text-blue-600 hover:text-blue-800 cursor-pointer">
                <i
                  className="fas fa-info-circle"
                  title="Current federal tax bracket"
                ></i>
              </span>
            </div>
            <div className="mt-1">
              <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">24%</div>
              <div className="text-sm text-gray-500 dark:text-gray-500">Federal Rate</div>
            </div>
          </div>
        </div>
      </div>

      {/* Goals Summary & Timeline */}
      <div className="space-y-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Financial Goals Summary
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            Client aims to retire by 2045 with $2M in savings. Short-term goals
            include building emergency fund and paying off student loans.
            Mid-term goals focus on college savings for children.
          </p>
        </div>

        <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg border">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Timeline</h3>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute h-1 w-full bg-gray-200 top-4"></div>

            {/* Timeline items */}
            <div className="relative flex justify-between">
              <TimelineItem
                status="complete"
                date="2023"
                label="Emergency Fund"
              />
              <TimelineItem
                status="in-progress"
                date="2024"
                label="Student Loans"
              />
              <TimelineItem
                status="upcoming"
                date="2025"
                label="College Fund"
              />
              <TimelineItem status="upcoming" date="2045" label="Retirement" />
            </div>
          </div>
        </div>
      </div>

      {/* Financial Health Analysis */}
      <div className="bg-gray-50 p-6 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Financial Health Analysis
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow-sm">
            <FinancialHealthCard healthScore={79} />
          </div>
          <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm">
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-4">
              Potential Improvement
            </h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>Current Score</span>
                  <span>79</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1">
                  <span>Potential Score</span>
                  <span>85</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div
                    className="h-2 bg-blue-500 rounded-full"
                    style={{ width: "79%" }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Advisor Action Items */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg border">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Advisor Action Items
          </h2>
          <button className="px-4 py-2 bg-[#131d80] text-white rounded-md hover:bg-[#0f1660]">
            Add Task
          </button>
        </div>
        <div className="space-y-4">
          <ActionItemCategory
            title="Reports & Documentation"
            items={[
              {
                text: "Generate Q1 Portfolio Review",
                due: "2024-03-31",
                status: "pending",
              },
              {
                text: "Update Risk Assessment",
                due: "2024-03-15",
                status: "in-progress",
              },
            ]}
          />
          <ActionItemCategory
            title="Client Communication"
            items={[
              {
                text: "Schedule Annual Review Meeting",
                due: "2024-04-01",
                status: "pending",
              },
              {
                text: "Send Tax Document Reminder",
                due: "2024-03-10",
                status: "complete",
              },
            ]}
          />
        </div>
      </div>

      {/* Client Action Items */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg border">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Client Action Items
          </h2>
          <button className="px-4 py-2 bg-[#131d80] text-white rounded-md hover:bg-[#0f1660]">
            Add Task
          </button>
        </div>
        <div className="space-y-4">
          <ActionItemCategory
            title="Required Documents"
            items={[
              {
                text: "Submit 2023 Tax Returns",
                due: "2024-04-15",
                status: "pending",
              },
              {
                text: "Complete Risk Questionnaire",
                due: "2024-03-20",
                status: "pending",
              },
            ]}
          />
          <ActionItemCategory
            title="Financial Planning"
            items={[
              {
                text: "Review Retirement Options",
                due: "2024-03-25",
                status: "in-progress",
              },
              {
                text: "Update Beneficiary Information",
                due: "2024-03-31",
                status: "pending",
              },
            ]}
          />
        </div>
      </div>

      {/* Follow-up Dates & Reminders */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg border">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Follow-up Dates & Reminders
          </h2>
          <button className="px-4 py-2 bg-[#131d80] text-white rounded-md hover:bg-[#0f1660]">
            View All
          </button>
        </div>
        <div className="space-y-4">
          {[
            {
              date: "2024-03-15",
              type: "meeting",
              title: "Portfolio Review Call",
              status: "upcoming",
            },
            {
              date: "2024-03-20",
              type: "reminder",
              title: "Document Collection Deadline",
              status: "pending",
            },
            {
              date: "2024-04-01",
              type: "meeting",
              title: "Tax Planning Session",
              status: "scheduled",
            },
          ].map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div className="flex items-center space-x-4">
                <div
                  className={`w-2 h-2 rounded-full ${
                    item.type === "meeting" ? "bg-blue-500" : "bg-yellow-500"
                  }`}
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-gray-100">{item.title}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">{item.date}</p>
                </div>
              </div>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  item.status === "upcoming"
                    ? "bg-yellow-100 text-yellow-800"
                    : item.status === "scheduled"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800 dark:text-gray-200"
                }`}
              >
                {item.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Helper component for timeline items
const TimelineItem = ({ status, date, label }) => {
  const getStatusColor = () => {
    switch (status) {
      case "complete":
        return "bg-green-500";
      case "in-progress":
        return "bg-yellow-500";
      case "upcoming":
        return "bg-gray-300";
      default:
        return "bg-gray-300";
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div
        className={`w-8 h-8 rounded-full ${getStatusColor()} relative z-10 flex items-center justify-center`}
      >
        {status === "complete" && (
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
      <div className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">{date}</div>
      <div className="text-xs text-gray-500 dark:text-gray-500">{label}</div>
    </div>
  );
};

// Helper Components
const ActionItemCategory = ({ title, items }) => (
  <div className="bg-gray-50 p-4 rounded-lg">
    <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-3">{title}</h3>
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={index}
          className="flex items-center justify-between bg-surface-default dark:bg-surface-default p-3 rounded-md"
        >
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={item.status === "complete"}
              className="h-4 w-4 text-blue-600"
            />
            <span className="text-gray-700 dark:text-gray-300">{item.text}</span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-500 dark:text-gray-500">Due: {item.due}</span>
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                item.status === "complete"
                  ? "bg-green-100 text-green-800"
                  : item.status === "in-progress"
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-gray-100 text-gray-800 dark:text-gray-200"
              }`}
            >
              {item.status}
            </span>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default SummaryActionItems;
